import * as React from 'react'

import cn from 'classnames/bind'
import type { Field } from 'factorial-form'

import { observer } from 'mobx-react'

import styles from './index.module.scss'

const cx = cn.bind(styles)
interface ChildrenProps {
  checked: boolean
  inputElement: unknown
}

export type BehaviourTypes = 'radio' | 'checkbox'
interface Props {
  field: Field
  readonly?: boolean
  onChange?: (value: unknown) => void
  skin?: BehaviourTypes
  behaviour?: BehaviourTypes
  inputAutoPositioned?: boolean
  name?: string
  value?: string
}
/**
 * Fancy checkbox
 * ==============
 * This component is used to represent `radio-buttons` or `checkbox`
 * with a Factorial look and feel.
 * This could be splitted in 2 components. But Is only changing a CSS class
 * and the change method. Not a big deal
 */

const CheckControl: React.FC<Props & Partial<React.ConsumerProps<ChildrenProps>>> = (props) => {
  const {
    value,
    name,
    children,
    readonly = false,
    skin = 'radio',
    field,
    behaviour = 'radio',
    inputAutoPositioned = true,
    onChange,
  } = props

  const isRadio = behaviour === 'radio'
  const checked = isRadio ? value === field._mapOut() : field.value

  const onClick = React.useCallback(
    (event: React.MouseEvent) => {
      // This method allow un-click a radio button
      if (!isRadio) return

      if (!checked) return

      event.preventDefault()
      event.stopPropagation()
      field.set(null)
    },
    [isRadio, checked, field]
  )

  const onChangeCheckbox = React.useCallback(() => {
    const value = !field.value
    field.mapAndSet(value)

    if (onChange) onChange(value)
  }, [field, onChange])

  const onChangeRadio = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      field.set(value)

      if (onChange) onChange(value)
    },
    [field, onChange]
  )

  const currentChecked = isRadio ? checked : field.value
  const handleChange = isRadio ? onChangeRadio : onChangeCheckbox
  const hasChildren = !!children
  const autopositioned = inputAutoPositioned && hasChildren

  let inputAttr: React.InputHTMLAttributes<HTMLInputElement> = {
    className: styles.input ?? '',
    type: behaviour,
    checked: currentChecked,
    disabled: readonly,
    onChange: handleChange,
  }

  if (value) {
    inputAttr = { ...inputAttr, value }
  }

  if (name) {
    inputAttr = { ...inputAttr, name }
  }

  const inputElement = () => (
    <div className={styles.inputWrapper}>
      <input {...inputAttr} />
      <div className={styles.controlIndicator} />
    </div>
  )

  return (
    <label
      onClick={onClick}
      className={cx('control', skin, {
        hasChildren,
        disabled: readonly,
        autopositioned,
      })}
    >
      {inputAutoPositioned && inputElement()}
      {hasChildren && (
        <div className={styles.children}>
          {children &&
            children({
              checked: currentChecked,
              inputElement,
            })}
        </div>
      )}
    </label>
  )
}

export default observer(CheckControl)
